import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Download, ArrowDownToLine, ArrowUpToLine, Wallet } from "lucide-react";
import * as XLSX from "xlsx";
import { trackUserAction } from "@/utils/tracking";
import { StatementChat } from "@/components/StatementChat";

type Txn = {
  id: string;
  txn_date: string;
  description_clean: string | null;
  counterparty: string | null;
  amount: number;
  currency: string;
  direction: "in" | "out";
  category: string;
  subcategory: string | null;
  balance_after: number | null;
};

const CATEGORY_LABEL: Record<string, string> = {
  investment: "Investment",
  ahmed_investment: "Founder investment",
  revenue: "Revenue",
  burn: "Operating expense",
  transfer: "Transfer",
  fee: "Bank fee / VAT",
  refund: "Refund",
  other: "Other",
};

const fmt = (n: number, ccy = "SAR") =>
  `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${ccy}`;

export default function Statement() {
  const [loading, setLoading] = useState(true);
  const [gateOk, setGateOk] = useState(false);
  const [txns, setTxns] = useState<Txn[]>([]);

  // filters
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [direction, setDirection] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { count } = await supabase
        .from("bank_statements")
        .select("id", { count: "exact", head: true })
        .eq("status", "committed");
      if (!count) { setGateOk(false); setLoading(false); return; }
      setGateOk(true);
      // Page through RPC results to bypass PostgREST's default 1000-row cap
      const PAGE = 1000;
      const all: Txn[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
          .rpc("get_transactions_public")
          .range(from, from + PAGE - 1);
        if (error) { console.error(error); break; }
        const chunk = (data as Txn[]) ?? [];
        all.push(...chunk);
        if (chunk.length < PAGE) break;
      }
      setTxns(all.sort((a, b) => b.txn_date.localeCompare(a.txn_date)));
      setLoading(false);
      trackUserAction("page_visit", { page_path: "/statement" });
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return txns.filter((t) => {
      if (category !== "all" && t.category !== category) return false;
      if (direction !== "all" && t.direction !== direction) return false;
      if (dateFrom && t.txn_date < dateFrom) return false;
      if (dateTo && t.txn_date > dateTo) return false;
      if (q) {
        const hay = `${t.counterparty ?? ""} ${t.description_clean ?? ""} ${t.subcategory ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [txns, search, category, direction, dateFrom, dateTo]);

  const kpis = useMemo(() => {
    const inflow = filtered.filter((t) => t.direction === "in").reduce((s, t) => s + Number(t.amount), 0);
    const outflow = filtered.filter((t) => t.direction === "out").reduce((s, t) => s + Number(t.amount), 0);
    const ccy = filtered[0]?.currency ?? "SAR";
    return { inflow, outflow, net: inflow - outflow, ccy };
  }, [filtered]);

  const aggregated = useMemo(() => {
    const map = new Map<string, { month: string; category: string; total: number }>();
    for (const t of filtered) {
      const month = t.txn_date.slice(0, 7);
      const key = `${month}|${t.category}`;
      const signed = t.direction === "in" ? Number(t.amount) : -Number(t.amount);
      const cur = map.get(key);
      if (cur) cur.total += signed;
      else map.set(key, { month, category: t.category, total: signed });
    }
    return Array.from(map.values()).sort((a, b) => b.month.localeCompare(a.month) || a.category.localeCompare(b.category));
  }, [filtered]);

  const downloadCsv = () => {
    const rows = filtered.map((t) => ({
      Date: t.txn_date,
      Counterparty: t.counterparty ?? "",
      Description: t.description_clean ?? "",
      Category: CATEGORY_LABEL[t.category] ?? t.category,
      Subcategory: t.subcategory ?? "",
      Direction: t.direction,
      Amount: t.direction === "out" ? -Number(t.amount) : Number(t.amount),
      Currency: t.currency,
      "Balance after": t.balance_after,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Statement");
    XLSX.writeFile(wb, `statement-${new Date().toISOString().slice(0, 10)}.csv`, { bookType: "csv" });
    trackUserAction("download", { file_name: "statement.csv" });
  };

  const downloadXlsx = () => {
    const rows = filtered.map((t) => ({
      Date: t.txn_date,
      Counterparty: t.counterparty ?? "",
      Description: t.description_clean ?? "",
      Category: CATEGORY_LABEL[t.category] ?? t.category,
      Subcategory: t.subcategory ?? "",
      Direction: t.direction,
      Amount: t.direction === "out" ? -Number(t.amount) : Number(t.amount),
      Currency: t.currency,
      "Balance after": t.balance_after,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Statement");
    XLSX.writeFile(wb, `statement-${new Date().toISOString().slice(0, 10)}.xlsx`);
    trackUserAction("download", { file_name: "statement.xlsx" });
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  if (!gateOk) {
    return (
      <div className="max-w-2xl mx-auto p-8">
        <Card>
          <CardHeader><CardTitle>Interactive Statement</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              The interactive bank statement will be available once the first statement is committed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-primary">Interactive Statement</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every transaction from the company's bank statements. Filter, search, and export.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total inflow</span>
              <ArrowDownToLine className="h-4 w-4 text-green-600" />
            </div>
            <div className="text-2xl font-semibold mt-2 text-green-600">{fmt(kpis.inflow, kpis.ccy)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total outflow</span>
              <ArrowUpToLine className="h-4 w-4 text-destructive" />
            </div>
            <div className="text-2xl font-semibold mt-2 text-destructive">{fmt(kpis.outflow, kpis.ccy)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Net</span>
              <Wallet className="h-4 w-4 text-primary" />
            </div>
            <div className={`text-2xl font-semibold mt-2 ${kpis.net >= 0 ? "text-green-600" : "text-destructive"}`}>
              {fmt(kpis.net, kpis.ccy)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <Input
              placeholder="Search counterparty / description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="md:col-span-2"
            />
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger><SelectValue placeholder="Direction" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">In & Out</SelectItem>
                <SelectItem value="in">Inflow only</SelectItem>
                <SelectItem value="out">Outflow only</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
            <div className="text-xs text-muted-foreground">
              Showing {filtered.length.toLocaleString()} of {txns.length.toLocaleString()} transactions
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={downloadCsv}><Download className="h-4 w-4 mr-1" /> CSV</Button>
              <Button variant="outline" size="sm" onClick={downloadXlsx}><Download className="h-4 w-4 mr-1" /> Excel</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <StatementChat
        endpoint="statement-chat"
        title="Ask AI about the statement"
        placeholder="Ask about inflows, burn, runway, monthly trends, or specific categories."
        suggestions={[
          "What was the monthly burn rate in the last 6 months?",
          "Summarize total inflows by category.",
          "Estimate remaining runway based on current balance and avg burn.",
          "Which months had the largest revenue inflows?",
        ]}
      />

      <Tabs defaultValue="detail">
        <TabsList>
          <TabsTrigger value="detail">Transactions</TabsTrigger>
          <TabsTrigger value="aggregated">Monthly summary</TabsTrigger>
        </TabsList>

        <TabsContent value="detail">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background border-b">
                    <tr className="text-left">
                      <th className="p-3">Date</th>
                      <th className="p-3">Counterparty</th>
                      <th className="p-3">Category</th>
                      <th className="p-3 text-right">Amount</th>
                      <th className="p-3 text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((t) => (
                      <tr key={t.id} className="border-b hover:bg-muted/40">
                        <td className="p-3 whitespace-nowrap text-muted-foreground">{t.txn_date}</td>
                        <td className="p-3 max-w-[320px] truncate" title={t.counterparty ?? ""}>
                          {t.counterparty ?? <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-xs">{CATEGORY_LABEL[t.category] ?? t.category}</Badge>
                          {t.subcategory && <span className="ml-2 text-xs text-muted-foreground">{t.subcategory}</span>}
                        </td>
                        <td className={`p-3 text-right whitespace-nowrap font-medium ${t.direction === "in" ? "text-green-600" : "text-destructive"}`}>
                          {t.direction === "out" ? "-" : "+"}{fmt(Number(t.amount), t.currency)}
                        </td>
                        <td className="p-3 text-right whitespace-nowrap text-muted-foreground">
                          {t.balance_after != null ? fmt(Number(t.balance_after), t.currency) : "—"}
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No transactions match.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="aggregated">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background border-b">
                    <tr className="text-left">
                      <th className="p-3">Month</th>
                      <th className="p-3">Category</th>
                      <th className="p-3 text-right">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregated.map((r) => (
                      <tr key={`${r.month}-${r.category}`} className="border-b hover:bg-muted/40">
                        <td className="p-3 whitespace-nowrap">{r.month}</td>
                        <td className="p-3">{CATEGORY_LABEL[r.category] ?? r.category}</td>
                        <td className={`p-3 text-right font-medium ${r.total >= 0 ? "text-green-600" : "text-destructive"}`}>
                          {fmt(r.total, kpis.ccy)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
